import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  Typography,
  Box,
  Checkbox,
  Grid,
  TableCell,
  TableRow,
  createFilterOptions,
} from '@mui/material'

import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material'
import { StateSchema } from '@/hooks/form/models'
import { BonusProvider } from '@/state/bonus-engine/models'

import AutocompleteCustom from '@/views/components/autocomplete-custom/index.component'
import NumberFormatCustom from '@/views/components/input-number-format/index.component'
import CurrenciesTableComponent from '@/views/components/currencies-table/currencies-table.components'

import { BonusType } from '@/enums'
import { authSelector } from '@/state/auth/slices/index.slices'
import { CurrencyCodeEnum } from '@/enums/currency-code.enum'

import { sentenceCase } from '@/utils'
import { BGAMING_PROVIDER_EXTERNAL_ID, MAX_FREE_SPIN_GAMES } from '@/constants'
import ExpirationComponent from '../expiration-time.component'
import GamesComponent from '../../common/games.component'

type Props = {
  handleOnChange: (name: string, value: any) => void
  handleOnChangeProvider: (name: string, value: any) => void
  state: StateSchema
  providers: BonusProvider[]
  bonusType: string
}

const CasinoRestrictionsComponent = ({
  handleOnChange,
  handleOnChangeProvider,
  state,
  providers,
  bonusType,
}: Props) => {
  const { t } = useTranslation()

  const isFreeSpins = bonusType === BonusType.FREE_SPINS

  let listProviders =
    providers && providers.length > 0
      ? providers
          .filter((item: any) => item.name !== undefined)
          .map((provider: any) => {
            return {
              label: provider.name,
              value: provider.externalID,
            }
          })
      : []

  // free spins are issued by BGaming only, the selector is locked on it
  if (isFreeSpins) {
    listProviders = listProviders.filter(
      (item: any) => Number(item.value) === BGAMING_PROVIDER_EXTERNAL_ID,
    )
  }
  // HERE
  // listProviders =
  //   bonusType === BonusType.BETSOFT_CASINO
  //     ? listProviders.filter((item) => item.value === 15)
  // : listProviders.filter(
  //     (item) =>
  //       item.value === 15 ||
  //       item.value === 34 ||
  //       item.value === 36 ||
  //       item.value === 93 ||
  //       item.value === 94 ||
  //       item.value === 95 ||
  //       item.value === 97 ||
  //       item.value === 101 ||
  //       item.value === 102 ||
  //       item.value === 108 ||
  //       item.value === 120 ||
  //       item.value === 126 ||
  //       item.value === 144 ||
  //       item.value === 145 ||
  //       item.value === 150 ||
  //       item.value === 151,
  //   )

  const allSelected = listProviders.length === state.providers.value.length

  const currencyHeadCells =
    bonusType !== BonusType.BETSOFT_CASINO
      ? ['baseCurrency', 'bonusMaxBet', 'bonusMaxWin']
      : ['baseCurrency', 'bonusMaxWin']

  const { brand } = useSelector(authSelector)

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      handleOnChangeProvider('providers', listProviders)
    } else {
      handleOnChangeProvider('providers', [])
    }
  }

  const handleToggleSelectAll = () => {
    handleSelectAll && handleSelectAll(!allSelected)
  }

  const handleChange = (event: any, selectedOptions: any[], reason: string) => {
    if (reason === 'selectOption' || reason === 'removeOption') {
      if (selectedOptions.find((option) => option.value === 'select-all')) {
        handleToggleSelectAll()
      } else if (reason === 'removeOption') {
        handleOnChangeProvider('providers', selectedOptions)
      } else {
        const findValue =
          selectedOptions.length > 0 &&
          state.providers.value.find(
            (item: any) =>
              item.label === selectedOptions[selectedOptions.length - 1].label,
          )

        if (findValue) {
          const removeValue = state.providers.value.filter(
            (provider: any) =>
              provider.label !==
              selectedOptions[selectedOptions.length - 1].label,
          )
          handleOnChangeProvider('providers', removeValue)
        } else {
          handleOnChangeProvider('providers', selectedOptions)
        }
      }
    } else if (reason === 'clear') {
      handleOnChangeProvider('providers', [])
    }
  }

  const filter = createFilterOptions()

  const providerValidation =
    bonusType === BonusType.CASINO_STANDARD
      ? state.providers?.value.length === 0 || state.providers.error.length > 0
      : state.providers?.error.length > 0

  return (
    <>
      <Typography sx={{ marginBottom: '20px' }}>
        {t('bonusStepRestrictions')}
      </Typography>
      <Typography fontWeight={700} gutterBottom sx={{ mb: '15px' }}>
        {t('eligibleGame')}
      </Typography>
      <Typography sx={{ marginTop: '15px', marginBottom: '4px' }}>
        {t('bonusStepCasino')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box maxWidth={400}>
            <AutocompleteCustom
              limitTags={2}
              multiple
              onChange={handleChange}
              chipTags
              freeSolo
              label={t('providers')}
              disabled={isFreeSpins || listProviders.length === 0}
              options={listProviders}
              value={state.providers.value || []}
              error={providerValidation}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              helperText={
                providerValidation
                  ? sentenceCase(
                      `${t('error.requiredFieldArray', {
                        min: 1,
                        field: 'provider',
                      })}`,
                      true,
                    )
                  : ''
              }
              getOptionLabel={(option) => option.label}
              filterOptions={(options: any, params: any) => {
                const filtered = filter(options, params)
                return [
                  { label: t('labels.selectAll'), value: 'select-all' },
                  ...filtered,
                ]
              }}
              renderOption={(props, option) => {
                const selectAllProps =
                  option.value === 'select-all' // To control the state of 'select-all' checkbox
                    ? { checked: allSelected }
                    : {}

                return (
                  <li {...props}>
                    <Checkbox
                      color="primary"
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBox fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={
                        state.providers.value
                          ? state.providers.value.filter(
                              (provider: any) =>
                                provider.label === option.label,
                            ).length > 0
                          : false
                      }
                      {...selectAllProps}
                    />
                    {option.value === 'select-all' ? (
                      <span className="select-all-items">
                        <em>{option.label}</em>
                      </span>
                    ) : (
                      `${option.label} - ${option.value}`
                    )}
                  </li>
                )
              }}
            />
          </Box>
        </Grid>
      </Grid>
      {/* only free spins restrict the bonus down to a list of games */}
      {isFreeSpins && (
        <>
          <Typography sx={{ marginTop: '20px', marginBottom: '4px' }}>
            {t('freeSpinGamesDescription', { max: MAX_FREE_SPIN_GAMES })}
          </Typography>
          <GamesComponent
            handleOnChange={handleOnChange}
            bonusType={bonusType}
            state={state}
          />
        </>
      )}
      {/* BGaming enforces its own bet and win limits on a free spins issue */}
      {!isFreeSpins && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box maxWidth={800} mb={2} mt={3}>
              <Typography fontWeight={700} gutterBottom sx={{ mb: '15px' }}>
                {bonusType !== BonusType.BETSOFT_CASINO
                  ? t('maxBetAndWin')
                  : t('maxWin')}
              </Typography>
              <CurrenciesTableComponent currencyHeadCells={currencyHeadCells}>
                {brand.currencies.map((currency) => (
                  <TableRow key={`restriction_${currency}`}>
                    <TableCell>
                      {currency === CurrencyCodeEnum.USD
                        ? `${currency} ${t('labels.currencyDefault')}`
                        : currency}
                    </TableCell>
                    {bonusType !== BonusType.BETSOFT_CASINO && (
                      <TableCell sx={{ width: 300 || 'auto' }}>
                        <NumberFormatCustom
                          name={`maxBet_${currency}`}
                          handleOnChange={handleOnChange}
                          state={state}
                          currency={currency}
                          style={{ padding: '16px 0' }}
                          prefix=""
                          fullwidth
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ width: 300 || 'auto' }}>
                      <NumberFormatCustom
                        name={`maxWin_${currency}`}
                        handleOnChange={handleOnChange}
                        state={state}
                        currency={currency}
                        style={{ padding: '16px 0' }}
                        prefix=""
                        fullwidth
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </CurrenciesTableComponent>
            </Box>
          </Grid>
        </Grid>
      )}
      <Box mb={2}>
        <ExpirationComponent
          handleOnChange={handleOnChange}
          state={state}
          required={isFreeSpins}
        />
      </Box>
      <Box mt={3} maxWidth={400}>
        <Typography fontWeight={700} sx={{ mb: '20px' }}>
          {t('maxBonusesAllowedDescription')}
        </Typography>
        <NumberFormatCustom
          name="maxBonusesAllowed"
          label={t('labels.maxBonusesAllowed')}
          handleOnChange={handleOnChange}
          state={state}
          prefix=""
          fullwidth
          InputPropsAdornment={{
            endAdornment: (
              <Box pr={1}>
                <Typography variant="body2" component="p">
                  {t('labels.lifetime')}
                </Typography>
              </Box>
            ),
          }}
        />
      </Box>
    </>
  )
}

export default CasinoRestrictionsComponent
